/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Webhook Detail Page - SuperAdmin Portal
 * ============================================================================
 *
 * View webhook configuration and delivery history.
 * Test webhooks, retry failed deliveries, and manage webhook settings.
 *
 * ============================================================================
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Webhook as WebhookIcon,
  ArrowLeft,
  RefreshCw,
  Play,
  Key,
  Copy,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  ExternalLink,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Zap,
  Activity,
  Timer,
  Calendar,
} from 'lucide-react';
import {
  useWebhook,
  useWebhookDeliveries,
  useTestWebhook,
  useRetryDelivery,
  useRegenerateSecret,
  useWebhookSecret,
  useResetCircuit,
} from '@/hooks/api';
import { WebhookDelivery, WebhookDeliveryStatus } from '@/types/superadmin.types';
import { useNotification } from '@/hooks/useNotification';
import { ConfirmationDialog } from '@/components/ui';
import { isApiError, formatErrorForDisplay } from '@/services/api/errors';

// Delivery Status Badge Component
const DeliveryStatusBadge: React.FC<{ status: WebhookDeliveryStatus; t: (key: string) => string }> = ({
  status,
  t,
}) => {
  const config = {
    PENDING: { bg: 'bg-yellow-500/15', text: 'text-yellow-500', icon: Clock },
    SUCCESS: { bg: 'bg-emerald-500/15', text: 'text-emerald-500', icon: CheckCircle },
    FAILED: { bg: 'bg-red-500/15', text: 'text-red-500', icon: XCircle },
    RETRYING: { bg: 'bg-blue-500/15', text: 'text-blue-500', icon: RefreshCw },
  };
  const { bg, text, icon: Icon } = config[status] || config.PENDING;

  return (
    <span className={`delivery-status-badge ${bg} ${text}`}>
      <Icon className="w-3 h-3" />
      {t(`webhooks.deliveryStatus.${status.toLowerCase()}`)}
    </span>
  );
};

// Stat Card Component
const StatCard: React.FC<{
  icon: React.ReactNode;
  value: string | number;
  label: string;
  color: string;
}> = ({ icon, value, label, color }) => (
  <div className="stat-card">
    <div className={`stat-icon ${color}`}>{icon}</div>
    <div className="stat-value">{value}</div>
    <div className="stat-label">{label}</div>
  </div>
);

// Payload Modal Component
const PayloadModal: React.FC<{
  isOpen: boolean;
  title: string;
  content: string | null | undefined;
  onClose: () => void;
}> = ({ isOpen, title, content, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content payload-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>
        <div className="modal-body">
          <pre className="payload-content">
            {content ? JSON.stringify(JSON.parse(content), null, 2) : 'No content'}
          </pre>
        </div>
      </div>
    </div>
  );
};

// Main Webhook Detail Page
export default function WebhookDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const { success, error } = useNotification();

  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<WebhookDeliveryStatus | ''>('');
  const [showSecret, setShowSecret] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [resetCircuitDialogOpen, setResetCircuitDialogOpen] = useState(false);
  const [retryDeliveryId, setRetryDeliveryId] = useState<string | null>(null);
  const [payloadModal, setPayloadModal] = useState<{ title: string; content: string | null | undefined } | null>(null);
  const [copiedSecret, setCopiedSecret] = useState(false);

  // Query hooks
  const { data: webhook, isLoading: webhookLoading, refetch: refetchWebhook } = useWebhook(id);
  const {
    data: deliveriesData,
    isLoading: deliveriesLoading,
    refetch: refetchDeliveries,
  } = useWebhookDeliveries(id, {
    page,
    size: 20,
    status: statusFilter || undefined,
  });
  const { data: secret, refetch: refetchSecret } = useWebhookSecret(id, showSecret);

  const testMutation = useTestWebhook();
  const retryMutation = useRetryDelivery();
  const regenerateMutation = useRegenerateSecret();
  const resetCircuitMutation = useResetCircuit();

  const deliveries = deliveriesData?.content || [];
  const totalPages = deliveriesData?.page?.totalPages || 1;
  const totalDeliveries = deliveriesData?.page?.totalElements || 0;

  // Calculate stats from deliveries
  const successCount = deliveries.filter(d => d.status === 'SUCCESS').length;
  const successRate = deliveries.length > 0 ? ((successCount / deliveries.length) * 100).toFixed(1) : '0';
  const avgDuration = deliveries.length > 0
    ? Math.round(deliveries.reduce((acc, d) => acc + (d.durationMs || 0), 0) / deliveries.length)
    : 0;

  // Handlers
  const handleTest = async () => {
    if (!id) return;
    try {
      await testMutation.mutateAsync(id);
      success(t('webhooks.messages.testSuccess'));
      setTestDialogOpen(false);
      refetchDeliveries();
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('webhooks.messages.testError') };
      error(message);
    }
  };

  const handleRetry = async () => {
    if (!retryDeliveryId) return;
    try {
      await retryMutation.mutateAsync(retryDeliveryId);
      success(t('webhooks.messages.retrySuccess'));
      setRetryDeliveryId(null);
      refetchDeliveries();
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('webhooks.messages.retryError') };
      error(message);
    }
  };

  const handleRegenerateSecret = async () => {
    if (!id) return;
    try {
      await regenerateMutation.mutateAsync(id);
      success(t('webhooks.messages.secretRegenerated'));
      setRegenerateDialogOpen(false);
      if (showSecret) {
        refetchSecret();
      }
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('webhooks.messages.updateError') };
      error(message);
    }
  };

  const handleResetCircuit = async () => {
    if (!id) return;
    try {
      await resetCircuitMutation.mutateAsync(id);
      success(t('webhooks.messages.resetCircuitSuccess'));
      setResetCircuitDialogOpen(false);
      refetchWebhook();
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('webhooks.messages.resetCircuitError') };
      error(message);
    }
  };

  const copySecret = async () => {
    if (secret) {
      await navigator.clipboard.writeText(secret);
      setCopiedSecret(true);
      success(t('webhooks.secret.copied'));
      setTimeout(() => setCopiedSecret(false), 2000);
    }
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatEvents = (events: string[]) => {
    if (events.includes('*') || events.length === 0) return 'All Events';
    return events.join(', ');
  };

  if (webhookLoading) {
    return (
      <div className="webhook-detail-page">
        <div className="loading-state">
          <div className="spinner" />
          <span>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (!webhook) {
    return (
      <div className="webhook-detail-page">
        <div className="empty-state">
          <WebhookIcon className="w-12 h-12" />
          <p>Webhook not found</p>
          <button onClick={() => navigate('/superadmin/webhooks')} className="btn btn-primary">
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  const isCircuitOpen = webhook.consecutiveFailures >= 5;

  return (
    <div className="webhook-detail-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <button onClick={() => navigate('/superadmin/webhooks')} className="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="page-title">
              <WebhookIcon className="w-6 h-6" /> {webhook.name}
            </h1>
            <p className="page-subtitle">{t('webhooks.detail.title')}</p>
          </div>
        </div>
        <div className="header-actions">
          <button onClick={() => { refetchWebhook(); refetchDeliveries(); }} className="btn btn-secondary">
            <RefreshCw className="w-4 h-4" />
            {t('common.refresh')}
          </button>
          <button onClick={() => setTestDialogOpen(true)} className="btn btn-primary">
            <Play className="w-4 h-4" />
            {t('webhooks.actions.test')}
          </button>
        </div>
      </div>

      {/* Circuit Breaker Alert */}
      {isCircuitOpen && (
        <div className="circuit-alert">
          <AlertTriangle className="w-5 h-5" />
          <div>
            <strong>{t('webhooks.status.circuitOpen')}</strong>
            <p>{t('webhooks.resetCircuitDialog.message')}</p>
          </div>
          <button onClick={() => setResetCircuitDialogOpen(true)} className="btn btn-warning">
            <RotateCcw className="w-4 h-4" />
            {t('webhooks.actions.resetCircuit')}
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid">
        <StatCard
          icon={<Activity className="w-5 h-5" />}
          value={totalDeliveries}
          label={t('webhooks.detail.stats.totalDeliveries')}
          color="stat-icon-blue"
        />
        <StatCard
          icon={<CheckCircle className="w-5 h-5" />}
          value={`${successRate}%`}
          label={t('webhooks.detail.stats.successRate')}
          color="stat-icon-green"
        />
        <StatCard
          icon={<Timer className="w-5 h-5" />}
          value={`${avgDuration}ms`}
          label={t('webhooks.detail.stats.avgDuration')}
          color="stat-icon-purple"
        />
        <StatCard
          icon={<Calendar className="w-5 h-5" />}
          value={formatDate(webhook.lastTriggeredAt)}
          label={t('webhooks.detail.stats.lastSuccess')}
          color="stat-icon-amber"
        />
      </div>

      <div className="content-grid">
        {/* Configuration Panel */}
        <div className="config-panel">
          <h3>{t('webhooks.detail.configuration')}</h3>

          <div className="config-field">
            <label>{t('webhooks.detail.fields.name')}</label>
            <span>{webhook.name}</span>
          </div>

          <div className="config-field">
            <label>{t('webhooks.detail.fields.url')}</label>
            <span className="url-value">
              <ExternalLink className="w-4 h-4" />
              {webhook.url}
            </span>
          </div>

          <div className="config-field">
            <label>{t('webhooks.detail.fields.status')}</label>
            <span className={`status-value ${isCircuitOpen ? 'status-error' : webhook.isActive ? 'status-active' : 'status-inactive'}`}>
              {isCircuitOpen ? (
                <><AlertTriangle className="w-4 h-4" /> {t('webhooks.status.circuitOpen')}</>
              ) : webhook.isActive ? (
                <><CheckCircle className="w-4 h-4" /> {t('webhooks.status.active')}</>
              ) : (
                <><XCircle className="w-4 h-4" /> {t('webhooks.status.inactive')}</>
              )}
            </span>
          </div>

          <div className="config-field">
            <label>{t('webhooks.detail.fields.events')}</label>
            <span className="events-value">{formatEvents(webhook.events)}</span>
          </div>

          <div className="config-field">
            <label>{t('webhooks.detail.fields.retryCount')}</label>
            <span>{webhook.retryCount}</span>
          </div>

          <div className="config-field">
            <label>{t('webhooks.detail.fields.timeout')}</label>
            <span>{webhook.timeoutSeconds}s</span>
          </div>

          <div className="config-field">
            <label>{t('webhooks.detail.fields.consecutiveFailures')}</label>
            <span className={webhook.consecutiveFailures > 0 ? 'text-error' : ''}>
              {webhook.consecutiveFailures}
            </span>
          </div>

          <div className="config-field">
            <label>{t('webhooks.detail.fields.created')}</label>
            <span>{formatDate(webhook.createdAt)}</span>
          </div>

          {/* Secret Section */}
          <div className="secret-section">
            <h4>{t('webhooks.secret.title')}</h4>
            <p className="secret-description">{t('webhooks.secret.description')}</p>

            <div className="secret-field">
              <div className="secret-value">
                {showSecret && secret ? (
                  <code>{secret}</code>
                ) : (
                  <code>••••••••••••••••••••••••••••••</code>
                )}
              </div>
              <div className="secret-actions">
                <button
                  onClick={() => setShowSecret(!showSecret)}
                  className="secret-btn"
                  title={showSecret ? 'Hide' : 'Show'}
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                {showSecret && secret && (
                  <button onClick={copySecret} className="secret-btn" title="Copy">
                    {copiedSecret ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                )}
                <button onClick={() => setRegenerateDialogOpen(true)} className="secret-btn" title="Regenerate">
                  <Key className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Deliveries Panel */}
        <div className="deliveries-panel">
          <div className="deliveries-header">
            <h3>{t('webhooks.detail.deliveryHistory')}</h3>
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value as WebhookDeliveryStatus | ''); setPage(0); }}
              className="filter-select"
            >
              <option value="">{t('webhooks.filters.allStatus')}</option>
              <option value="SUCCESS">{t('webhooks.deliveryStatus.success')}</option>
              <option value="FAILED">{t('webhooks.deliveryStatus.failed')}</option>
              <option value="PENDING">{t('webhooks.deliveryStatus.pending')}</option>
              <option value="RETRYING">{t('webhooks.deliveryStatus.retrying')}</option>
            </select>
          </div>

          <div className="deliveries-list">
            {deliveriesLoading ? (
              <div className="deliveries-loading">
                <div className="spinner" />
              </div>
            ) : deliveries.length === 0 ? (
              <div className="deliveries-empty">
                <Zap className="w-10 h-10" />
                <p>{t('webhooks.deliveries.empty')}</p>
                <span>{t('webhooks.deliveries.emptyDescription')}</span>
              </div>
            ) : (
              deliveries.map(delivery => (
                <div key={delivery.id} className="delivery-item">
                  <div className="delivery-header">
                    <div className="delivery-event">
                      <span className="event-type">{delivery.eventType}</span>
                      <DeliveryStatusBadge status={delivery.status} t={t} />
                    </div>
                    <div className="delivery-meta">
                      <span className="delivery-time">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDate(delivery.createdAt)}
                      </span>
                      {delivery.durationMs && (
                        <span className="delivery-duration">
                          <Timer className="w-3.5 h-3.5" />
                          {delivery.durationMs}ms
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="delivery-details">
                    <div className="detail-row">
                      <span className="detail-label">{t('webhooks.deliveries.attempt')}:</span>
                      <span>{delivery.attemptNumber}</span>
                    </div>
                    {delivery.responseStatus && (
                      <div className="detail-row">
                        <span className="detail-label">{t('webhooks.deliveries.responseStatus')}:</span>
                        <span className={delivery.responseStatus >= 200 && delivery.responseStatus < 300 ? 'text-success' : 'text-error'}>
                          {delivery.responseStatus}
                        </span>
                      </div>
                    )}
                    {delivery.errorMessage && (
                      <div className="detail-row error-row">
                        <span className="detail-label">{t('webhooks.deliveries.error')}:</span>
                        <span className="text-error">{delivery.errorMessage}</span>
                      </div>
                    )}
                  </div>

                  <div className="delivery-actions">
                    <button
                      onClick={() => setPayloadModal({ title: t('webhooks.deliveries.payload'), content: delivery.payload })}
                      className="delivery-action-btn"
                    >
                      {t('webhooks.deliveries.viewPayload')}
                    </button>
                    {delivery.responseBody && (
                      <button
                        onClick={() => setPayloadModal({ title: t('webhooks.deliveries.response'), content: delivery.responseBody })}
                        className="delivery-action-btn"
                      >
                        {t('webhooks.deliveries.viewResponse')}
                      </button>
                    )}
                    {delivery.status === 'FAILED' && (
                      <button
                        onClick={() => setRetryDeliveryId(delivery.id)}
                        className="delivery-action-btn retry-btn"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        {t('webhooks.deliveries.retry')}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="deliveries-pagination">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="pagination-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="page-indicator">{page + 1} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="pagination-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <ConfirmationDialog
        isOpen={testDialogOpen}
        title={t('webhooks.testDialog.title')}
        message={t('webhooks.testDialog.message')}
        variant="confirm"
        onConfirm={handleTest}
        onCancel={() => setTestDialogOpen(false)}
        isLoading={testMutation.isPending}
        confirmText={t('webhooks.testDialog.confirm')}
      />

      <ConfirmationDialog
        isOpen={regenerateDialogOpen}
        title={t('webhooks.secret.regenerate')}
        message={t('webhooks.secret.regenerateConfirm')}
        variant="warning"
        onConfirm={handleRegenerateSecret}
        onCancel={() => setRegenerateDialogOpen(false)}
        isLoading={regenerateMutation.isPending}
        confirmText={t('webhooks.secret.regenerate')}
      />

      <ConfirmationDialog
        isOpen={resetCircuitDialogOpen}
        title={t('webhooks.resetCircuitDialog.title')}
        message={t('webhooks.resetCircuitDialog.message')}
        variant="warning"
        onConfirm={handleResetCircuit}
        onCancel={() => setResetCircuitDialogOpen(false)}
        isLoading={resetCircuitMutation.isPending}
        confirmText={t('webhooks.resetCircuitDialog.confirm')}
      />

      <ConfirmationDialog
        isOpen={!!retryDeliveryId}
        title={t('webhooks.deliveries.retry')}
        message="Retry this failed delivery?"
        variant="confirm"
        onConfirm={handleRetry}
        onCancel={() => setRetryDeliveryId(null)}
        isLoading={retryMutation.isPending}
        confirmText={t('webhooks.deliveries.retry')}
      />

      {/* Payload Modal */}
      <PayloadModal
        isOpen={!!payloadModal}
        title={payloadModal?.title || ''}
        content={payloadModal?.content || null}
        onClose={() => setPayloadModal(null)}
      />

      <style>{webhookDetailStyles}</style>
    </div>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const webhookDetailStyles = `
  .webhook-detail-page {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  /* Page Header */
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .back-btn {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    color: var(--color-text-primary);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .back-btn:hover {
    background: var(--color-bg-hover);
  }

  .page-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font-heading);
    font-size: 22px;
    font-weight: 700;
    color: var(--color-text-primary);
  }

  .page-subtitle {
    font-size: 13px;
    color: var(--color-text-secondary);
    margin-top: 4px;
  }

  .header-actions {
    display: flex;
    gap: 12px;
  }

  /* Button */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0 16px;
    height: 40px;
    font-size: 13px;
    font-weight: 500;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-primary {
    background: var(--color-brand-primary);
    color: white;
  }

  .btn-primary:hover {
    background: var(--color-brand-primary-hover);
  }

  .btn-secondary {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    color: var(--color-text-primary);
  }

  .btn-secondary:hover {
    background: var(--color-bg-hover);
  }

  .btn-warning {
    background: #F59E0B;
    color: white;
  }

  .btn-warning:hover {
    background: #D97706;
  }

  /* Circuit Alert */
  .circuit-alert {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: 12px;
    color: #EF4444;
  }

  .circuit-alert strong {
    display: block;
    font-weight: 600;
  }

  .circuit-alert p {
    font-size: 13px;
    opacity: 0.8;
    margin-top: 2px;
  }

  .circuit-alert .btn {
    margin-left: auto;
    flex-shrink: 0;
  }

  /* Stats Grid */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }

  .stat-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    padding: 16px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    margin-bottom: 12px;
  }

  .stat-icon-blue { background: rgba(59, 130, 246, 0.15); color: #3B82F6; }
  .stat-icon-green { background: rgba(16, 185, 129, 0.15); color: #10B981; }
  .stat-icon-purple { background: rgba(139, 92, 246, 0.15); color: #8B5CF6; }
  .stat-icon-amber { background: rgba(245, 158, 11, 0.15); color: #F59E0B; }

  .stat-value {
    font-size: 20px;
    font-weight: 700;
    font-family: var(--font-mono);
    color: var(--color-text-primary);
  }

  .stat-label {
    font-size: 12px;
    color: var(--color-text-muted);
    margin-top: 4px;
  }

  /* Content Grid */
  .content-grid {
    display: grid;
    grid-template-columns: 380px 1fr;
    gap: 24px;
  }

  /* Config Panel */
  .config-panel {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    padding: 20px;
  }

  .config-panel h3 {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin-bottom: 20px;
  }

  .config-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 0;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .config-field:last-of-type {
    border-bottom: none;
  }

  .config-field label {
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-muted);
  }

  .config-field span {
    font-size: 14px;
    color: var(--color-text-primary);
    word-break: break-all;
  }

  .url-value {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--color-text-secondary);
  }

  .status-value {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .status-active { color: #10B981; }
  .status-inactive { color: #6B7280; }
  .status-error { color: #EF4444; }

  .events-value {
    font-size: 13px;
    line-height: 1.5;
  }

  .text-error { color: #EF4444; }
  .text-success { color: #10B981; }

  /* Secret Section */
  .secret-section {
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid var(--color-border-subtle);
  }

  .secret-section h4 {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin-bottom: 4px;
  }

  .secret-description {
    font-size: 12px;
    color: var(--color-text-muted);
    margin-bottom: 12px;
  }

  .secret-field {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
  }

  .secret-value {
    flex: 1;
    overflow: hidden;
  }

  .secret-value code {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--color-text-secondary);
    word-break: break-all;
  }

  .secret-actions {
    display: flex;
    gap: 4px;
  }

  .secret-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 6px;
    color: var(--color-text-muted);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .secret-btn:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  /* Deliveries Panel */
  .deliveries-panel {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .deliveries-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .deliveries-header h3 {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .filter-select {
    height: 36px;
    padding: 0 12px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 6px;
    font-size: 13px;
    color: var(--color-text-primary);
    outline: none;
  }

  .deliveries-list {
    flex: 1;
    overflow-y: auto;
    max-height: 600px;
  }

  .deliveries-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px;
  }

  .deliveries-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px;
    color: var(--color-text-muted);
    text-align: center;
  }

  .deliveries-empty p {
    margin-top: 12px;
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-secondary);
  }

  .deliveries-empty span {
    font-size: 13px;
    margin-top: 4px;
  }

  /* Delivery Item */
  .delivery-item {
    padding: 16px 20px;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .delivery-item:last-child {
    border-bottom: none;
  }

  .delivery-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .delivery-event {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .event-type {
    font-size: 13px;
    font-weight: 500;
    color: var(--color-text-primary);
  }

  .delivery-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
  }

  .delivery-meta {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .delivery-time,
  .delivery-duration {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: var(--color-text-muted);
  }

  .delivery-details {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    font-size: 13px;
  }

  .detail-row {
    display: flex;
    gap: 6px;
  }

  .detail-label {
    color: var(--color-text-muted);
  }

  .error-row {
    flex-basis: 100%;
  }

  .delivery-actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
  }

  .delivery-action-btn {
    padding: 6px 12px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 6px;
    font-size: 12px;
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .delivery-action-btn:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .retry-btn {
    color: var(--color-brand-primary);
  }

  /* Pagination */
  .deliveries-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 12px 20px;
    border-top: 1px solid var(--color-border-subtle);
  }

  .pagination-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 6px;
    color: var(--color-text-primary);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .pagination-btn:hover:not(:disabled) {
    background: var(--color-bg-hover);
  }

  .pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .page-indicator {
    font-size: 13px;
    color: var(--color-text-secondary);
  }

  /* Loading & Empty States */
  .loading-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px;
    gap: 16px;
    color: var(--color-text-muted);
  }

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--color-border-subtle);
    border-top-color: var(--color-brand-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Modal */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 24px;
  }

  .modal-content {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 16px;
    width: 100%;
    max-width: 600px;
    max-height: 80vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .payload-modal {
    max-width: 800px;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .modal-header h2 {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .modal-close {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    font-size: 24px;
    color: var(--color-text-muted);
    cursor: pointer;
    border-radius: 6px;
  }

  .modal-close:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .modal-body {
    padding: 20px;
    overflow-y: auto;
  }

  .payload-content {
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    padding: 16px;
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--color-text-secondary);
    white-space: pre-wrap;
    word-break: break-all;
    overflow-x: auto;
    max-height: 400px;
  }

  /* Responsive */
  @media (max-width: 1200px) {
    .content-grid {
      grid-template-columns: 1fr;
    }

    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 768px) {
    .page-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }

    .header-actions {
      width: 100%;
    }

    .header-actions .btn {
      flex: 1;
      justify-content: center;
    }

    .circuit-alert {
      flex-direction: column;
      text-align: center;
    }

    .circuit-alert .btn {
      margin-left: 0;
      width: 100%;
    }

    .delivery-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    .delivery-actions {
      flex-wrap: wrap;
    }
  }
`;
